import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  Link,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Close,
  History as HistoryIcon,
  LocationOn,
  Search,
  SearchRounded as SearchRoundedIcon,
  Visibility,
} from '@mui/icons-material';
import Loader from '../../../common/loader';
import { getActivityLogsAdmin } from '../../../services/admin.service';
import { ActivityLog } from '../../../validation/admin.validation';

const ACTIONS = [
  'login',
  'logout',
  'lead_create',
  'lead_update',
  'lead_delete',
  'deal_create',
  'deal_update',
  'deal_delete',
  'document_upload',
  'document_update',
  'document_delete',
  'kyc_create',
  'kyc_update',
  'deal_assign',
];

const formatAction = (action: string) =>
  action
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

const formatDate = (value: string) =>
  value
    ? new Intl.DateTimeFormat('en-US', {
        dateStyle: 'medium',
        timeStyle: 'short',
      }).format(new Date(value))
    : '-';

const formatLocation = (log: ActivityLog) => {
  if (!log.location) return '-';

  const locationName = [log.location.city, log.location.region, log.location.country]
    .filter(Boolean)
    .join(', ');

  if (locationName) {
    return locationName;
  }

  return `${log.location.latitude.toFixed(5)}, ${log.location.longitude.toFixed(5)}`;
};

const getLocationHref = (log: ActivityLog) => {
  if (!log.location) return '';
  return `https://www.google.com/maps?q=${log.location.latitude},${log.location.longitude}`;
};

const isUpdateAction = (action: string) => action.endsWith('_update');

const formatDataValue = (value: any) => {
  if (value === undefined || value === null || value === '') return '-';
  if (value instanceof Date) return value.toLocaleString();
  if (typeof value === 'object') return JSON.stringify(value, null, 2);
  return String(value);
};

const getChangedFields = (log: ActivityLog) => {
  const oldData = log.oldData || {};
  const newData = log.newData || {};
  const keys = Array.from(new Set([...Object.keys(oldData), ...Object.keys(newData)]));

  return keys
    .filter((key) => JSON.stringify(oldData[key]) !== JSON.stringify(newData[key]))
    .map((key) => ({
      field: key,
      oldValue: oldData[key],
      newValue: newData[key],
    }));
};

const AdminActivityLogs: React.FC = () => {
  const [logs, setLogs] = useState<ActivityLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [action, setAction] = useState('');
  const [selectedLog, setSelectedLog] = useState<ActivityLog | null>(null);
  const [paginationData, setPaginationData] = useState({
    page: 1,
    limit: 25,
    total: 0,
  });
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const fetchLogs = async (params: {
    page: number;
    limit: number;
    search: string;
    action: string;
  }) => {
    setLoading(true);
    try {
      const response = await getActivityLogsAdmin(
        params.page,
        params.limit,
        params.search,
        params.action,
      );

      if (response) {
        setLogs(response.data);
        setPaginationData({
          page: response.page,
          limit: response.limit,
          total: response.total,
        });
      }
    } catch (error) {
      console.error('Error fetching activity logs:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs({
      page: paginationData.page,
      limit: paginationData.limit,
      search,
      action,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationData.page]);

  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setPaginationData((prev) => ({ ...prev, page: 1 }));
      fetchLogs({
        page: 1,
        limit: paginationData.limit,
        search,
        action,
      });
    }, 500);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, action]);

  const handlePageChange = (_: any, value: number) => {
    setPaginationData((prev) => ({ ...prev, page: value }));
  };

  const selectedChanges = selectedLog ? getChangedFields(selectedLog) : [];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="mx-auto">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-5 mb-4 lg:mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex flex-col sm:flex-row gap-3 flex-1">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                <input
                  type="text"
                  placeholder="Search actor, action, or entity..."
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <FormControl size="small" sx={{ minWidth: 190 }}>
                <InputLabel id="activity-action-label">Action</InputLabel>
                <Select
                  labelId="activity-action-label"
                  label="Action"
                  value={action}
                  onChange={(event) => setAction(event.target.value)}
                >
                  <MenuItem value="">All actions</MenuItem>
                  {ACTIONS.map((item) => (
                    <MenuItem key={item} value={item}>
                      {formatAction(item)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>

            <Chip
              icon={<HistoryIcon />}
              label={`Total Logs: ${paginationData.total}`}
              variant="filled"
              color="primary"
              size="medium"
              sx={{
                fontWeight: 'bold',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                '& .MuiChip-icon': {
                  color: 'white',
                },
              }}
            />
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-12">
            <Loader />
          </div>
        ) : logs.length === 0 ? (
          <Box className="flex flex-col items-center justify-center py-32 text-center text-gray-500 bg-white rounded-lg border border-gray-200">
            <SearchRoundedIcon className="text-gray-400" sx={{ fontSize: 48 }} />
            <Typography variant="h6" className="mt-4 text-gray-700">
              No Activity Logs Found
            </Typography>
            <Typography variant="body2" className="text-gray-500">
              Try adjusting your filters.
            </Typography>
          </Box>
        ) : (
          <>
            <div className="overflow-x-auto mb-6 lg:mb-8">
              <TableContainer component={Paper} sx={{ borderRadius: 3, minWidth: 1100 }}>
                <Table sx={{ borderCollapse: 'collapse' }}>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f9fafb' }}>
                      {['Time', 'Actor', 'Role', 'Action', 'Entity', 'Location', 'Changes'].map((header) => (
                        <TableCell key={header} sx={{ fontWeight: 'bold', border: '1px solid #e5e7eb' }}>
                          {header}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {logs.map((log) => (
                      <TableRow key={log.id} hover>
                        <TableCell sx={{ border: '1px solid #e5e7eb', whiteSpace: 'nowrap' }}>
                          {formatDate(log.createdAt)}
                        </TableCell>
                        <TableCell sx={{ border: '1px solid #e5e7eb' }}>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {log.actorEmail || '-'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {log.actorId || ''}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ border: '1px solid #e5e7eb' }}>
                          {log.actorRole || '-'}
                        </TableCell>
                        <TableCell sx={{ border: '1px solid #e5e7eb' }}>
                          <Chip label={formatAction(log.action)} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell sx={{ border: '1px solid #e5e7eb' }}>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {log.entityType}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {log.entityId || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ border: '1px solid #e5e7eb', minWidth: 210 }}>
                          {log.location ? (
                            <Link
                              href={getLocationHref(log)}
                              target="_blank"
                              rel="noopener noreferrer"
                              underline="hover"
                              className="inline-flex items-center gap-1"
                            >
                              <LocationOn fontSize="small" />
                              {formatLocation(log)}
                            </Link>
                          ) : (
                            '-'
                          )}
                          {log.location?.accuracy ? (
                            <Typography variant="caption" color="text.secondary" display="block">
                              Accuracy: {Math.round(log.location.accuracy)}m
                            </Typography>
                          ) : null}
                          {log.location?.source ? (
                            <Typography variant="caption" color="text.secondary" display="block">
                              Source: {log.location.source === 'ip' ? 'IP address' : 'Browser'}
                            </Typography>
                          ) : null}
                        </TableCell>
                        <TableCell sx={{ border: '1px solid #e5e7eb', whiteSpace: 'nowrap' }}>
                          {isUpdateAction(log.action) && log.oldData && log.newData ? (
                            <Tooltip title="View Changes">
                              <IconButton
                                color="info"
                                size="small"
                                onClick={() => setSelectedLog(log)}
                              >
                                <Visibility fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            '-'
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </div>

            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg shadow-sm">
              <Pagination
                count={Math.ceil(paginationData.total / paginationData.limit)}
                page={paginationData.page}
                onChange={handlePageChange}
                color="primary"
                shape="rounded"
                size="medium"
                className="self-center sm:self-auto"
              />
              <div className="text-xs sm:text-sm text-gray-600 text-center sm:text-right">
                Showing {(paginationData.page - 1) * paginationData.limit + 1} to{' '}
                {Math.min(paginationData.page * paginationData.limit, paginationData.total)} of{' '}
                {paginationData.total} results
              </div>
            </div>
          </>
        )}
      </div>

      <Dialog
        open={Boolean(selectedLog)}
        onClose={() => setSelectedLog(null)}
        fullWidth
        maxWidth="lg"
        PaperProps={{
          sx: {
            borderRadius: 2,
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle
          sx={{
            px: 3,
            py: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="h5" component="span" className="font-bold text-gray-800">
              {selectedLog ? `${formatAction(selectedLog.action)} Changes` : 'Changes'}
            </Typography>
            {selectedLog ? (
              <Typography variant="body2" color="text.secondary">
                {selectedLog.entityType} {selectedLog.entityId ? `- ${selectedLog.entityId}` : ''}
              </Typography>
            ) : null}
          </Box>
          <Tooltip title="Close">
            <IconButton
              aria-label="close"
              onClick={() => setSelectedLog(null)}
              sx={{ color: (theme) => theme.palette.grey[500] }}
            >
              <Close />
            </IconButton>
          </Tooltip>
        </DialogTitle>
        <Divider />
        <DialogContent className="custom-scrollbar" sx={{ p: 3, bgcolor: '#f9fafb' }}>
          <div className="flex flex-wrap gap-2 mb-4">
            {selectedLog?.actorEmail ? (
              <Chip label={selectedLog.actorEmail} size="small" variant="outlined" />
            ) : null}
            {selectedLog?.actorRole ? (
              <Chip label={selectedLog.actorRole} size="small" variant="outlined" />
            ) : null}
            {selectedLog?.createdAt ? (
              <Chip label={formatDate(selectedLog.createdAt)} size="small" variant="outlined" />
            ) : null}
          </div>
          {selectedChanges.length === 0 ? (
            <Box className="bg-white border border-gray-200 rounded-lg px-4 py-8 text-center">
              <Typography variant="body2" color="text.secondary">
                No field-level changes found.
              </Typography>
            </Box>
          ) : (
            <TableContainer component={Paper} sx={{ borderRadius: 2, maxHeight: 560, border: '1px solid #e5e7eb' }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f9fafb' }}>
                    {['Field', 'Old Data', 'New Data'].map((header) => (
                      <TableCell
                        key={header}
                        sx={{
                          fontWeight: 700,
                          bgcolor: '#f9fafb',
                          borderBottom: '1px solid #e5e7eb',
                        }}
                      >
                        {header}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedChanges.map((change) => (
                    <TableRow key={change.field} hover>
                      <TableCell sx={{ width: 220, fontWeight: 700, verticalAlign: 'top', borderBottom: '1px solid #e5e7eb' }}>
                        {change.field}
                      </TableCell>
                      <TableCell sx={{ verticalAlign: 'top', maxWidth: 420, borderBottom: '1px solid #e5e7eb' }}>
                        <Typography
                          component="pre"
                          variant="caption"
                          sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0 }}
                        >
                          {formatDataValue(change.oldValue)}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ verticalAlign: 'top', maxWidth: 420, borderBottom: '1px solid #e5e7eb' }}>
                        <Typography
                          component="pre"
                          variant="caption"
                          sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0 }}
                        >
                          {formatDataValue(change.newValue)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, bgcolor: '#fff' }}>
          <Button
            onClick={() => setSelectedLog(null)}
            startIcon={<Close fontSize="small" />}
            variant="outlined"
            sx={{ borderRadius: 2, fontWeight: 600, textTransform: 'none' }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default AdminActivityLogs;
